import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const useKeyHold = (
  keys: string[],
  keyDownCallback: (event: Event) => void,
  keyUpCallback: (event: Event) => void,
  node: HTMLElement | null = null,
) => {
  // Implement the callback ref pattern
  const keyDownCallbackRef = useRef(keyDownCallback);
  const keyUpCallbackRef = useRef(keyUpCallback);
  useLayoutEffect(() => {
    keyDownCallbackRef.current = keyDownCallback;
    keyUpCallbackRef.current = keyUpCallback;
  });
  const [isHoldActive, setIsHoldActive] = useState(false);

  // Handle what happens on key down
  const handleKeyDown = useCallback(
    (event: Event) => {
      // Do not handle key press when a `textarea` has focus
      if (
        document.activeElement?.nodeName === "INPUT" ||
        document.activeElement?.nodeName === "TEXTAREA"
      ) {
        return;
      }

      if ((event as KeyboardEvent).repeat) return;
      // Check if one of the key is part of the ones we want
      if (keys.some((key) => (event as KeyboardEvent).key === key)) {
        keyDownCallbackRef.current(event);
        setIsHoldActive(true);
      }
    },
    [keys],
  );

  // Handle what happens on key up
  const handleKeyUp = useCallback(
    (event: Event) => {
      // Check if one of the key is part of the ones we want
      if (keys.some((key) => (event as KeyboardEvent).key === key)) {
        keyUpCallbackRef.current(event);
        setIsHoldActive(false);
      }
    },
    [keys],
  );

  // Trigger the keyup event if the user switches tabs while holding the key down
  const handleVisibilityChange = useCallback(() => {
    if (isHoldActive && document.visibilityState === "hidden") {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  // Trigger the keyup event if the user clicks outside the window while holding the key down
  const handleBlurEvent = useCallback(() => {
    if (isHoldActive) {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  // Trigger the keyup event if the user clicks elsewhere in the classroom while holding the key down
  const handleMouseDown = useCallback(() => {
    if (isHoldActive) {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  useEffect(() => {
    // Target is either the provided node or the document
    const targetNode = node ?? document;
    // Attach the event listener
    targetNode.addEventListener("keydown", handleKeyDown);
    targetNode.addEventListener("keyup", handleKeyUp);
    targetNode.addEventListener("mousedown", handleMouseDown);
    targetNode.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlurEvent);

    // Remove the event listener
    return () => {
      targetNode.removeEventListener("keydown", handleKeyDown);
      targetNode.removeEventListener("keyup", handleKeyUp);
      targetNode.removeEventListener("mousedown", handleMouseDown);
      targetNode.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlurEvent);
    };
  }, [handleBlurEvent, handleKeyDown, handleKeyUp, handleMouseDown, handleVisibilityChange, node]);
};

export { useKeyHold };
